import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import partyEngineService from '../../../services/partyEngineService';
import { useDialog } from '../../../context/DialogContext';
import { useUser } from '../../../context/UserContext';
import RunRow from './RunRow';
import ConceptCard, { ConceptPlaceholder } from './ConceptCard';
import EditConceptModal from './EditConceptModal';
import EventApplyPage from '../../../pages/EventApplyPage';
import {
  PartyEngineStyles, RunStatusPill, ModeChip, Chevron, XIcon, Check, Btn,
  money, fmtWhen, elapsed,
} from './ui';

const ACTIVE = new Set(['queued', 'researching', 'verifying_venues', 'generating', 'rendering', 'publishing']);
const STAGES = [
  { key: 'research', label: 'RESEARCH', statuses: ['researching', 'verifying_venues'] },
  { key: 'concepts', label: 'CONCEPTS', statuses: ['generating'] },
  { key: 'posters', label: 'POSTERS', statuses: ['rendering'] },
  { key: 'review', label: 'REVIEW', statuses: ['awaiting_review'] },
  { key: 'publish', label: 'PUBLISH', statuses: ['publishing'] },
];
const ORDER = ['queued', 'researching', 'verifying_venues', 'generating', 'rendering', 'awaiting_review', 'publishing', 'completed'];

/* ─────────────────────────────── Health strip ─────────────────────────────── */
function HealthStrip({ health }) {
  if (!health) return null;
  const problems = [];
  if (!health.enabled) problems.push('ENGINE OFF — ENABLE PARTY_ENGINE_ENABLED IN SETTINGS');
  if (!health.llmConfigured) problems.push('LLM KEY MISSING — SET ANTHROPIC:APIKEY (OR OPENAI:APIKEY) ON THE SERVER');
  if (!health.imagesConfigured) problems.push('OPENAI KEY MISSING — POSTERS WILL BE SKIPPED');
  if (problems.length === 0) {
    return (
      <div className="rounded-[20px] bg-white font-mono font-bold uppercase" style={{ padding: '10px 14px', fontSize: 12, letterSpacing: '0.05em' }}>
        ENGINE ON<span style={{ color: '#9CA3AF' }}> · </span>KEYS OK<span style={{ color: '#9CA3AF' }}> · </span>
        {health.activeRuns} RUN{health.activeRuns === 1 ? '' : 'S'} ACTIVE
        <span style={{ color: '#9CA3AF' }}> · </span><span style={{ color: '#6B7280' }}>{(health.llmModel || '').toUpperCase()}</span>
      </div>
    );
  }
  return (
    <div className="bg-white font-mono" style={{ padding: '10px 14px', border: '2px solid #F9B82C' }}>
      {problems.map((p, i) => (
        <div key={p} className="uppercase" style={{ fontSize: i === 0 ? 12 : 11, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? '#000' : '#6B7280', letterSpacing: '0.05em', marginTop: i === 0 ? 0 : 4 }}>{p}</div>
      ))}
    </div>
  );
}

/* ─────────────────────────────── Start a run ─────────────────────────────── */
function StartRunPanel({ health, onStarted }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [region, setRegion] = useState(null);
  const [mode, setMode] = useState('review');
  const [count, setCount] = useState(5);
  const [inviteAll, setInviteAll] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(-1);
  const max = health?.maxConcepts || 5;

  useEffect(() => {
    if (!query.trim() || region) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      const r = await partyEngineService.searchRegions(query.trim());
      if (r.success) { setResults(r.data || []); setActive(-1); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, region]);

  const engineOff = health && !health.enabled;
  const busyRegion = region?.hasActiveRun;
  const disabled = engineOff || !region || busyRegion || starting;
  let reason = null;
  if (engineOff) reason = 'DISABLED — ENGINE OFF';
  else if (busyRegion) reason = `${region.name.toUpperCase()} ALREADY HAS AN ACTIVE RUN.`;
  else if (!region) reason = 'PICK A REGION.';

  const start = async () => {
    setStarting(true); setError(null);
    const r = await partyEngineService.startRun(region.id, { mode, targetCount: count, includeAllHosts: inviteAll });
    setStarting(false);
    if (!r.success) { setError(r.error || 'Could not start the run.'); return; }
    onStarted(r.data);
  };

  const onKey = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    if (e.key === 'Enter' && active >= 0) { e.preventDefault(); setRegion(results[active]); setResults([]); }
  };

  return (
    <div className="rounded-[24px] bg-white font-mono overflow-hidden">
      <div className="border-b border-[#F2F2F2] font-bold uppercase" style={{ padding: '12px 14px', fontSize: 14, letterSpacing: '0.05em' }}>START A RUN</div>
      <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ maxWidth: 520 }}>
          <span className="block font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#6B7280', marginBottom: 6 }}>REGION</span>
          {region ? (
            <div className="flex items-center justify-between gap-3 rounded-[16px] bg-[#F7F7F7]" style={{ padding: '10px 12px' }}>
              <div className="min-w-0">
                <div className="font-bold" style={{ fontSize: 14 }}>{region.name}</div>
                <div className="uppercase" style={{ fontSize: 10, color: '#6B7280' }}>
                  {region.population ? `POP ${region.population.toLocaleString()}` : 'POP —'} · {(region.timeZone || 'TZ UNKNOWN').toUpperCase()}
                </div>
              </div>
              <button type="button" onClick={() => { setRegion(null); setQuery(''); }} className="hover:opacity-80" aria-label="Clear region"><XIcon /></button>
            </div>
          ) : (
            <div className="relative">
              <input
                className="pe-input uppercase"
                style={{ padding: '10px 12px', letterSpacing: '0.05em' }}
                placeholder="SEARCH A CITY…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKey}
              />
              {results.length > 0 && (
                <div className="absolute left-0 right-0 bg-white z-20" style={{ border: '2px solid #000', borderTop: 'none', boxShadow: '4px 4px 0 0 #000' }}>
                  {results.map((r, i) => (
                    <button
                      type="button"
                      key={r.id}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => { setRegion(r); setResults([]); }}
                      className="w-full text-left"
                      style={{ padding: '10px 12px', borderTop: i === 0 ? 'none' : '2px solid #000', background: i === active ? '#F5F5F5' : '#fff' }}
                    >
                      <div className="font-bold uppercase" style={{ fontSize: 13, letterSpacing: '0.05em' }}>{r.name}{r.hasActiveRun ? ' · RUN IN PROGRESS' : ''}</div>
                      <div className="uppercase" style={{ fontSize: 10, color: '#6B7280' }}>
                        {r.population ? `POP ${r.population.toLocaleString()}` : 'POP —'} · {(r.timeZone || 'TZ UNKNOWN').toUpperCase()}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-6">
          <div>
            <span className="block font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#6B7280', marginBottom: 6 }}>MODE</span>
            <div className="flex">
              {['review', 'auto'].map((m, i) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setMode(m)}
                  className="font-bold uppercase transition-colors duration-200"
                  style={{ padding: '9px 18px', fontSize: 12, letterSpacing: '0.05em', border: '2px solid #000', borderLeft: i === 1 ? 'none' : undefined, background: mode === m ? '#000' : '#fff', color: mode === m ? '#fff' : '#000' }}
                >
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="block font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#6B7280', marginBottom: 6 }}>CONCEPTS</span>
            <div className="flex">
              <input
                className="text-center font-mono"
                style={{ width: 56, border: '2px solid #000', padding: '9px 10px', fontSize: 14, outline: 'none', borderRadius: 0 }}
                type="number" min={1} max={max} value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(max, parseInt(e.target.value, 10) || 1)))}
              />
              <div className="flex flex-col" style={{ width: 26, border: '2px solid #000', borderLeft: 'none' }}>
                <button type="button" onClick={() => setCount((c) => Math.min(max, c + 1))} className="flex-1 hover:bg-black hover:text-white transition-colors duration-200 font-bold" style={{ borderBottom: '2px solid #000', fontSize: 12 }} aria-label="More">+</button>
                <button type="button" onClick={() => setCount((c) => Math.max(1, c - 1))} className="flex-1 hover:bg-black hover:text-white transition-colors duration-200 font-bold" style={{ fontSize: 12 }} aria-label="Fewer">−</button>
              </div>
            </div>
            <span className="block uppercase" style={{ fontSize: 10, color: '#9CA3AF', marginTop: 6 }}>MAX {max}</span>
          </div>
        </div>

        <button type="button" onClick={() => setInviteAll((v) => !v)} className="flex items-center gap-3 text-left self-start">
          <span className="flex items-center justify-center flex-none" style={{ width: 18, height: 18, border: '2px solid #000', background: inviteAll ? '#000' : '#fff' }}>
            {inviteAll && <Check />}
          </span>
          <span className="font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.05em' }}>INVITE ALL HOSTS PLATFORM-WIDE</span>
          <span className="uppercase" style={{ fontSize: 10, color: '#9CA3AF' }}>DEFAULT: THIS REGION ONLY</span>
        </button>

        <div className="flex items-center gap-4 flex-wrap">
          <Btn variant="primary" size="lg" disabled={disabled} onClick={start}>
            {busyRegion ? 'RUN IN PROGRESS' : starting ? 'STARTING…' : '[ RUN ENGINE ]'}
          </Btn>
          {reason && <span className="uppercase" style={{ fontSize: 11, color: '#6B7280' }}>{reason}</span>}
          {error && <span className="uppercase" style={{ fontSize: 11, color: '#DC2626' }}>{error}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Preview ─────────────────────────────── */
/**
 * CLICK TO PREVIEW — the real public event page, fed the concept as an unpublished
 * EventDto, inside a full-screen sheet. The page is inert; this bar is the only control.
 */
function PreviewSheet({ concept, onClose }) {
  const [dto, setDto] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    partyEngineService.previewConcept(concept.id).then((r) => {
      if (cancelled) return;
      if (r.success) setDto(r.data); else setError(r.error || 'Could not build the preview.');
    });
    return () => { cancelled = true; };
  }, [concept.id]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 safe-inset z-50 bg-white flex flex-col font-mono" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between gap-3 border-b border-[#F2F2F2] bg-black text-white flex-none" style={{ padding: '10px 16px' }}>
        <span className="font-bold uppercase truncate" style={{ fontSize: 12, letterSpacing: '0.05em' }}>
          PREVIEW · HOW GUESTS WILL SEE IT · NOT PUBLISHED
          <span className="hidden sm:inline" style={{ color: '#9CA3AF' }}> · {concept.title}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="font-bold uppercase border-2 border-white bg-black text-white hover:bg-white hover:text-black transition-colors duration-200 flex-none"
          style={{ fontSize: 11, padding: '6px 12px', letterSpacing: '0.05em' }}
        >
          CLOSE
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {error && <div className="uppercase" style={{ padding: 24, fontSize: 12, color: '#DC2626' }}>{error}</div>}
        {!dto && !error && <div className="uppercase" style={{ padding: 24, fontSize: 12, color: '#6B7280', letterSpacing: '0.05em' }}>BUILDING PREVIEW…</div>}
        {dto && <EventApplyPage previewEvent={dto} embedded />}
      </div>
    </div>
  );
}

/* ─────────────────────────────── Run detail ─────────────────────────────── */
function Stepper({ run }) {
  const idx = ORDER.indexOf(run.status);
  const failedOrCanceled = run.status === 'failed' || run.status === 'canceled';
  return (
    <div style={{ background: '#000', display: 'flex', gap: 2 }}>
      {STAGES.map((s) => {
        const stageIdx = ORDER.indexOf(s.statuses[0]);
        const lastIdx = ORDER.indexOf(s.statuses[s.statuses.length - 1]);
        const skipped = s.key === 'review' && run.mode === 'auto';
        let state = 'future';
        if (run.status === 'completed' || idx > lastIdx || (skipped && idx >= ORDER.indexOf('publishing'))) state = 'done';
        else if (idx >= stageIdx && idx <= lastIdx && !failedOrCanceled) state = 'current';
        if (failedOrCanceled && idx > stageIdx) state = 'done';
        return (
          <div key={s.key} className="flex-1 flex flex-col items-center justify-center" style={{ padding: '10px 8px', background: state === 'done' ? '#000' : '#fff', minHeight: 44 }}>
            <span className="font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: state === 'done' ? '#fff' : state === 'current' ? '#000' : '#9CA3AF' }}>{s.label}</span>
            {state === 'current' && <div className="pe-pulse" style={{ width: '70%', height: 5, background: '#000', marginTop: 6 }} />}
          </div>
        );
      })}
    </div>
  );
}

function SnapshotSheet({ researchJson, hostsInvited }) {
  const r = useMemo(() => { try { return researchJson ? JSON.parse(researchJson) : null; } catch { return null; } }, [researchJson]);
  if (!r) return <div className="uppercase" style={{ padding: '10px 16px', fontSize: 11, color: '#9CA3AF' }}>NO RESEARCH YET.</div>;
  const cats = (r.CategoryStats || r.categoryStats || []).slice(0, 3);
  const venues = r.Venues || r.venues || [];
  const pro = venues.filter((v) => (v.Source || v.source) === 'pro_listing').length;
  const meta = r.VenueMeta || r.venueMeta;
  const considered = meta ? (meta.VenuesConsidered ?? meta.venuesConsidered) : null;
  const rejected = meta ? (meta.VenuesRejected ?? meta.venuesRejected) : null;
  const unknownCount = meta ? (meta.VenuesUnknown ?? meta.venuesUnknown) : null;
  const rows = [
    ['SEASON', (r.Season || r.season || '').toUpperCase()],
    ['UPCOMING EVENTS', (r.Upcoming || r.upcoming || []).length],
    ['TOP CATEGORY BY FILL', cats[0] ? `${(cats[0].Category || cats[0].category).toUpperCase()} ${Math.round(cats[0].AvgFillPercent ?? cats[0].avgFillPercent ?? 0)}%` : '—'],
    ['VENUES VERIFIED FOR HIRE', considered != null ? `${venues.length} OF ${considered} CHECKED` : venues.length],
    ['SECOND CATEGORY', cats[1] ? `${(cats[1].Category || cats[1].category).toUpperCase()} ${Math.round(cats[1].AvgFillPercent ?? cats[1].avgFillPercent ?? 0)}%` : '—'],
    ['HOSTS ELIGIBLE', r.EligibleHosts ?? r.eligibleHosts ?? hostsInvited ?? 0],
    ['THIRD CATEGORY', cats[2] ? `${(cats[2].Category || cats[2].category).toUpperCase()} ${Math.round(cats[2].AvgFillPercent ?? cats[2].avgFillPercent ?? 0)}%` : '—'],
    ['PRO LISTINGS', `${pro} OF ${venues.length}`],
    ...(considered != null ? [['NOT HIREABLE / UNKNOWN', `${rejected} / ${unknownCount}`]] : []),
  ];
  return (
    <div style={{ background: '#000', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 2 }}>
      {rows.map(([k, v]) => (
        <div key={k} className="bg-white flex justify-between gap-3" style={{ padding: '9px 16px' }}>
          <span style={{ fontSize: 11, color: '#6B7280' }}>{k}</span>
          <span className="font-bold" style={{ fontSize: 11 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function RunDetail({ runId, onBack, onChanged, canEdit, canPublish }) {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useDialog();
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editError, setEditError] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  const initialised = useRef(false);

  const load = useCallback(async () => {
    const r = await partyEngineService.getRun(runId);
    if (!r.success) { setError(r.error || 'Run not found.'); return null; }
    setRun(r.data);
    if (!initialised.current) { initialised.current = true; setSnapshotOpen(r.data.status === 'awaiting_review'); }
    return r.data;
  }, [runId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!run || !ACTIVE.has(run.status)) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [run, load]);
  useEffect(() => {
    if (!run || run.isTerminal) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [run]);

  const act = async (fn, okMessage) => {
    setBusy(true);
    const r = await fn();
    setBusy(false);
    if (!r.success) { await showAlert({ title: 'FAILED', message: r.error || 'Could not do that.', type: 'error', theme: 'admin' }); return false; }
    if (okMessage && r.data?.message && r.data.message !== 'OK') await showAlert({ title: okMessage, message: r.data.message, theme: 'admin' });
    await load(); onChanged?.();
    return true;
  };

  const onConceptAction = async (action, concept) => {
    switch (action) {
      case 'approve': case 'unapprove': case 'reject': case 'retry':
        // optimistic pill/footer swap
        setRun((prev) => prev && ({ ...prev, concepts: prev.concepts.map((c) => c.id === concept.id ? { ...c, status: action === 'approve' || action === 'retry' ? 'approved' : action === 'reject' ? 'rejected' : 'draft' } : c) }));
        await act(() => partyEngineService.conceptAction(concept.id, action));
        break;
      case 'regenerate-poster':
        setRun((prev) => prev && ({ ...prev, concepts: prev.concepts.map((c) => c.id === concept.id ? { ...c, posterPending: true } : c) }));
        await act(() => partyEngineService.conceptAction(concept.id, 'regenerate-poster'));
        break;
      case 'edit': setEditing(concept); setEditError(null); break;
      case 'preview': setPreviewing(concept); break;
      case 'view-event': navigate(`/host/events/${concept.eventId}`); break;
      case 'revoke-claim': {
        const ok = await showConfirm({
          title: 'REVOKE CLAIM?',
          message: `Escrow of ${money(concept.eventPledgedCents)} moves back to the house account. @${concept.claimedByHandle || 'the host'} is notified.`,
          confirmLabel: 'REVOKE',
          variant: 'danger',
          theme: 'admin',
        });
        if (ok) await act(() => partyEngineService.revokeClaim(concept.eventId));
        break;
      }
      default: break;
    }
  };

  const publishApproved = async () => {
    const n = run.conceptsApproved;
    const ok = await showConfirm({
      title: `PUBLISH ${n} APPROVED CONCEPT${n === 1 ? '' : 'S'}?`,
      message: `This posts ${n} public Party Starter${n === 1 ? '' : 's'} in ${run.regionName} and notifies every eligible host${run.includeAllHosts ? ' on Portal' : ' in the region'}.`,
      confirmLabel: 'PUBLISH',
      variant: 'primary',
      theme: 'admin',
    });
    if (ok) await act(() => partyEngineService.publishApproved(run.id));
  };

  const cancelRun = async () => {
    const ok = await showConfirm({
      title: 'CANCEL RUN?',
      message: `Nothing is published and ${money(Math.round(run.estimatedCostUsd * 100))} already spent is not refunded.`,
      confirmLabel: 'CANCEL RUN',
      cancelLabel: 'KEEP RUNNING',
      variant: 'danger',
      theme: 'admin',
    });
    if (ok) await act(() => partyEngineService.cancelRun(run.id));
  };

  const saveEdit = async (payload) => {
    setBusy(true);
    const r = await partyEngineService.updateConcept(editing.id, payload);
    setBusy(false);
    if (!r.success) { setEditError(r.error || 'Could not save.'); return; }
    setEditing(null);
    await load();
  };

  if (error) return <div className="rounded-[20px] bg-white font-mono uppercase" style={{ padding: 16, fontSize: 12, color: '#DC2626' }}>{error}</div>;
  if (!run) return <div className="font-mono uppercase" style={{ fontSize: 12, color: '#6B7280', letterSpacing: '0.05em' }}>LOADING…</div>;

  const approved = run.conceptsApproved;
  const generating = ACTIVE.has(run.status) && run.status !== 'publishing';
  const total = run.targetCount;
  const placeholders = generating && run.status !== 'rendering' ? Math.max(0, total - run.concepts.length) : 0;
  const tally = run.status === 'generating'
    ? `${run.concepts.length} OF ${total} GENERATED · GENERATING CONCEPTS…`
    : run.status === 'rendering'
      ? `${run.concepts.length} OF ${total} GENERATED · RENDERING POSTERS…`
      : `${run.concepts.length} TOTAL · ${approved} APPROVED · ${run.conceptsDraft} DRAFT · ${run.conceptsRejected} REJECTED`;
  const publishReady = (run.status === 'awaiting_review' || run.status === 'completed') && approved > 0;
  const showPublish = !run.isTerminal || (run.status === 'completed' && approved > 0);
  const costLabel = run.isTerminal ? 'TOTAL COST' : 'COST SO FAR';
  const timeLabel = run.isTerminal ? `RAN ${elapsed(run.startedAt || run.createdAt, run.finishedAt)}` : `ELAPSED ${elapsed(run.startedAt || run.createdAt)}`;

  return (
    <div className="font-mono" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button type="button" onClick={onBack} className="self-start font-bold uppercase hover:opacity-80" style={{ fontSize: 11, color: '#6B7280', letterSpacing: '0.05em' }}>← ALL RUNS</button>

      <div className="overflow-hidden rounded-[24px] bg-white">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" style={{ padding: 16 }}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold uppercase" style={{ fontSize: 24, letterSpacing: '0.05em' }}>{run.regionName}</span>
              <ModeChip mode={run.mode} suffix=" MODE" />
              <RunStatusPill status={run.status} />
            </div>
            <div className="flex flex-wrap uppercase" style={{ fontSize: 11, color: '#6B7280', gap: '6px 18px', marginTop: 8 }}>
              <span>STARTED BY @{(run.requestedByHandle || 'admin').toUpperCase()}</span>
              <span>{fmtWhen(run.createdAt)}</span>
              <span>{timeLabel}</span>
              <span className="font-bold" style={{ color: '#000' }}>{costLabel} {money(Math.round(run.estimatedCostUsd * 100))}</span>
              {run.lastError && !run.isTerminal && <span style={{ color: '#DC2626' }}>RETRYING — {run.lastError.toUpperCase()}</span>}
              {run.status === 'failed' && run.lastError && <span style={{ color: '#DC2626' }}>{run.lastError.toUpperCase()}</span>}
            </div>
          </div>
          {(canPublish || canEdit) && (
            <div className="flex flex-wrap gap-2 flex-none">
              {canPublish && showPublish && (
                <Btn variant="primary" size="md" disabled={!publishReady || busy} onClick={publishApproved}>PUBLISH APPROVED ({approved})</Btn>
              )}
              {canEdit && run.conceptsPublished > 0 && <Btn variant="secondary" size="md" disabled={busy} onClick={() => act(() => partyEngineService.resendInvites(run.id), 'INVITES')}>RESEND INVITES</Btn>}
              {canEdit && !run.isTerminal && <Btn variant="danger" size="md" disabled={busy} onClick={cancelRun}>CANCEL RUN</Btn>}
            </div>
          )}
        </div>
        <div className="border-t border-[#F2F2F2]"><Stepper run={run} /></div>
        {run.status === 'completed' ? (
          <div className="border-t border-[#F2F2F2]" style={{ background: '#000', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
            {[['PUBLISHED', `${run.conceptsPublished} / ${run.concepts.length}`], ['HOSTS INVITED', run.hostsInvited], ['CLAIMED', run.conceptsClaimed], ['ESCROW HELD', money(run.escrowHeldCents)]].map(([k, v]) => (
              <div key={k} className="bg-white" style={{ padding: '10px 16px' }}>
                <div className="uppercase" style={{ fontSize: 10, color: '#6B7280' }}>{k}</div>
                <div className="font-bold" style={{ fontSize: 18 }}>{v}</div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <button type="button" onClick={() => setSnapshotOpen((o) => !o)} className="pe-toggle w-full flex items-center justify-between border-t border-[#F2F2F2] bg-white" style={{ padding: '11px 16px' }}>
              <span className="font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.05em' }}>RESEARCH SNAPSHOT</span>
              <Chevron open={snapshotOpen} />
            </button>
            {snapshotOpen && <div className="border-t border-[#F2F2F2]"><SnapshotSheet researchJson={run.researchJson} hostsInvited={run.hostsInvited} /></div>}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-bold uppercase" style={{ fontSize: 18, letterSpacing: '0.05em' }}>CONCEPTS</span>
        <span className="uppercase" style={{ fontSize: 11, color: '#6B7280', letterSpacing: '0.05em' }}>{tally}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 items-start" style={{ gap: 20 }}>
        {run.concepts.map((c) => (
          <ConceptCard
            key={c.id}
            concept={{ ...c, status: generating && c.posterPending && run.status !== 'rendering' ? c.status : c.status, hostsInvited: run.hostsInvited }}
            onAction={onConceptAction}
            busy={busy}
            canEdit={canEdit}
            canPublish={canPublish}
          />
        ))}
        {Array.from({ length: placeholders }).map((_, i) => (
          <ConceptPlaceholder key={`ph-${i}`} index={run.concepts.length + i + 1} total={total} label={run.status === 'researching' || run.status === 'queued' ? 'RESEARCHING…' : run.status === 'verifying_venues' ? 'VERIFYING VENUES…' : 'GENERATING CONCEPTS…'} />
        ))}
        {run.concepts.length === 0 && placeholders === 0 && (
          <div className="rounded-[24px] bg-white font-mono uppercase text-center lg:col-span-2" style={{ padding: '40px 14px', fontSize: 12, color: '#6B7280', letterSpacing: '0.05em' }}>NO CONCEPTS.</div>
        )}
      </div>

      {previewing && <PreviewSheet concept={previewing} onClose={() => setPreviewing(null)} />}
      {editing && (
        <EditConceptModal
          concept={editing}
          venues={run.venueOptions || []}
          timeZone={run.timeZone}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
          busy={busy}
          serverError={editError}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────── Panel root ─────────────────────────────── */
/**
 * ENGINES › PARTY ENGINE — spec/sections.md §18. Health strip → START A RUN (`party_engine`
 * · create) → RUNS → run detail under the list when one is open (nested route
 * `/admin/party-engine/runs/:runId`). Old `?tab=partyengine&run=` links are redirected.
 */
export default function PartyEnginePanel() {
  const navigate = useNavigate();
  const { runId: openRunId } = useParams();
  const [searchParams] = useSearchParams();
  const { can } = useUser();
  const canCreate = can('party_engine', 'create');
  const canEdit = can('party_engine', 'edit');
  const canPublish = can('party_engine', 'publish');
  const [health, setHealth] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const detailRef = useRef(null);

  // Legacy deep link: ?run={id} → the nested route.
  const legacyRun = searchParams.get('run');
  useEffect(() => {
    if (legacyRun && !openRunId) navigate(`/admin/party-engine/runs/${legacyRun}`, { replace: true });
  }, [legacyRun, openRunId, navigate]);

  const load = useCallback(async () => {
    const [h, r] = await Promise.all([partyEngineService.getHealth(), partyEngineService.listRuns({ take: 50 })]);
    if (h.success) setHealth(h.data);
    if (r.success) setRuns(r.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (openRunId || !runs.some((r) => ACTIVE.has(r.status))) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [runs, openRunId, load]);
  useEffect(() => {
    if (openRunId && detailRef.current) detailRef.current.scrollIntoView?.({ block: 'start' });
  }, [openRunId]);

  const openRun = (id) => navigate(`/admin/party-engine/runs/${id}`);
  const back = () => { navigate('/admin/party-engine'); load(); };

  const recent = runs.filter((r) => Date.now() - new Date(r.createdAt) < 30 * 86400000).length;

  return (
    <div className="font-mono" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PartyEngineStyles />
      <HealthStrip health={health} />
      {canCreate && <StartRunPanel health={health} onStarted={(run) => { setRuns((prev) => [run, ...prev]); openRun(run.id); }} />}
      <div className="overflow-hidden rounded-[24px] bg-white">
        <div className="flex items-center justify-between" style={{ padding: '12px 14px' }}>
          <span className="font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.05em' }}>RUNS</span>
          <span className="uppercase" style={{ fontSize: 10, color: '#9CA3AF', letterSpacing: '0.05em' }}>{recent} RUN{recent === 1 ? '' : 'S'} · LAST 30 DAYS</span>
        </div>
        {loading ? (
          <div className="border-t border-[#F2F2F2] uppercase text-center" style={{ padding: '32px 14px', fontSize: 11, color: '#6B7280' }}>LOADING…</div>
        ) : runs.length === 0 ? (
          <div className="border-t border-[#F2F2F2] text-center" style={{ padding: '56px 14px' }}>
            <div className="font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.1em' }}>NO RUNS YET.</div>
            <div className="uppercase" style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>{canCreate ? 'PICK A REGION ABOVE TO START ONE.' : 'NOTHING HAS RUN HERE.'}</div>
          </div>
        ) : runs.map((r) => <RunRow key={r.id} run={r} onOpen={openRun} active={r.id === openRunId} />)}
      </div>
      {openRunId && (
        <div ref={detailRef} style={{ scrollMarginTop: 16 }}>
          <RunDetail key={openRunId} runId={openRunId} onBack={back} onChanged={load} canEdit={canEdit} canPublish={canPublish} />
        </div>
      )}
    </div>
  );
}
